// P1 — Price Tiers admin page. Mirrors JobCategoriesList layout (TVP2 §9
// styling: bordered Search filter card → bordered action bar + table card).
// Bootstrap palette (Add green · Reset/Delete grey).
//
// Tiers are seeded with a single BASIC row by migration 00019; operator
// creates the real STANDARD/EXPERT (or chosen variant) tiers here, then
// reassigns categories to them via the Job Categories page.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";

interface PriceTierRow {
  id: string;
  code: string;
  label: string;
  description: string | null;
  base_price_cents: number;
  est_minutes: number;
  sort_order: number;
  is_active: boolean;
}

interface FormState {
  id?: string;
  code: string;
  label: string;
  description: string;
  base_price_cents: string;
  est_minutes: string;
  sort_order: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  code: "",
  label: "",
  description: "",
  base_price_cents: "",
  est_minutes: "",
  sort_order: "0",
  is_active: true,
};

function formatHKD(cents: number): string {
  return `HK$${(cents / 100).toLocaleString("en-HK")}`;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "tier_code_taken":
      return "A tier with that code already exists.";
    case "tier_in_use":
      return "Cannot disable a tier that is still assigned to active categories.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

export function PriceTiersList() {
  const { show, ToastEl } = useToast();
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const list = usePagedQuery<PriceTierRow>("price-tiers", {
    initialPagination: { page: 0, pageSize: 10 },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const basePriceCents = Number(editing.base_price_cents);
      const estMinutes = Number(editing.est_minutes);
      const sortOrder = Number(editing.sort_order);
      if (
        !Number.isFinite(basePriceCents) ||
        basePriceCents < 1000 ||
        basePriceCents > 1_000_000
      ) {
        throw new Error("Base price must be between HK$10 and HK$10,000 (in cents)");
      }
      if (
        !Number.isFinite(estMinutes) ||
        estMinutes < 5 ||
        estMinutes > 600
      ) {
        throw new Error("Estimated minutes must be between 5 and 600");
      }
      if (!Number.isFinite(sortOrder) || sortOrder < 0) {
        throw new Error("Sort order must be a non-negative integer");
      }
      const body: Record<string, unknown> = {
        code: editing.code.trim().toUpperCase(),
        label: editing.label.trim(),
        description: editing.description.trim() || undefined,
        base_price_cents: basePriceCents,
        est_minutes: estMinutes,
        sort_order: sortOrder,
      };
      if (editing.id) {
        body.is_active = editing.is_active;
        await apiFetch(`/v1/admin/price-tiers/${editing.id}`, {
          method: "PATCH",
          body,
        });
      } else {
        await apiFetch("/v1/admin/price-tiers", { method: "POST", body });
      }
    },
    onSuccess: () => {
      show("Saved", "success");
      setEditing(null);
      setFormError(null);
      list.invalidate();
    },
    onError: (err) => setFormError(friendlyError(err, "Save failed")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean; reason?: string }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/price-tiers/${id}`, { method: "DELETE" });
          results.push({ id, ok: true });
        } catch (err) {
          results.push({ id, ok: false, reason: friendlyError(err, "Delete failed") });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Disabled ${okCount} of ${results.length}`
          : `Disabled ${okCount} of ${results.length}; ${failCount} failed (some still in use?)`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const columns: DataTableColumn<PriceTierRow>[] = [
    { field: "code", headerName: "Code" },
    { field: "label", headerName: "Label" },
    {
      field: "base_price_cents",
      headerName: "Base price",
      render: (r) => formatHKD(r.base_price_cents),
    },
    {
      field: "est_minutes",
      headerName: "Est. minutes",
      render: (r) => `${r.est_minutes} min`,
    },
    { field: "sort_order", headerName: "Sort" },
    {
      field: "is_active",
      headerName: "Status",
      render: (r) => (r.is_active ? "Active" : "Inactive"),
    },
  ];

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const primaryBtnDialog = {
    ...baseBtn,
    px: 3,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };

  const isCreating = editing !== null && !editing.id;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() => {
              setFormError(null);
              setEditing({ ...EMPTY_FORM });
            }}
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={selected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Disable
          </Button>
        </Stack>

        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowCount={list.data?.total ?? 0}
            paginationModel={list.paginationModel}
            onPaginationModelChange={list.setPaginationModel}
            getRowId={(r) => r.id}
            checkboxSelection
            selectedIds={selected}
            onSelectionChange={setSelected}
            loading={list.isLoading}
            bordered={false}
            rowActions={(row) => (
              <Stack direction="row" spacing={0}>
                <IconButton
                  size="small"
                  aria-label="Edit"
                  onClick={() => {
                    setFormError(null);
                    setEditing({
                      id: row.id,
                      code: row.code,
                      label: row.label,
                      description: row.description ?? "",
                      base_price_cents: String(row.base_price_cents),
                      est_minutes: String(row.est_minutes),
                      sort_order: String(row.sort_order),
                      is_active: row.is_active,
                    });
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Disable"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          />
        </Box>
      </Paper>

      <Dialog
        open={editing !== null}
        onClose={() => {
          setEditing(null);
          setFormError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isCreating ? "Add Price Tier" : "Edit Price Tier"}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Code (uppercase, A-Z + underscore, 2-20 chars)"
              variant="standard"
              required
              fullWidth
              disabled={!isCreating}
              inputProps={{ maxLength: 20 }}
              value={editing?.code ?? ""}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, code: e.target.value.toUpperCase() } : s))
              }
              helperText={isCreating ? "Cannot be changed after create" : ""}
            />
            <TextField
              label="Label"
              variant="standard"
              required
              fullWidth
              inputProps={{ maxLength: 80 }}
              value={editing?.label ?? ""}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, label: e.target.value } : s))
              }
            />
            <TextField
              label="Description (optional)"
              variant="standard"
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              value={editing?.description ?? ""}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, description: e.target.value } : s))
              }
            />
            <TextField
              label="Base price in cents (HK$10 = 1000, HK$10,000 = 1,000,000)"
              variant="standard"
              required
              fullWidth
              type="number"
              inputProps={{ min: 1000, max: 1_000_000, step: 100 }}
              value={editing?.base_price_cents ?? ""}
              onChange={(e) =>
                setEditing((s) =>
                  s ? { ...s, base_price_cents: e.target.value } : s,
                )
              }
              helperText={
                editing?.base_price_cents
                  ? `≈ ${formatHKD(Number(editing.base_price_cents) || 0)}`
                  : ""
              }
            />
            <TextField
              label="Estimated minutes (5-600, guidance only — never used for billing)"
              variant="standard"
              required
              fullWidth
              type="number"
              inputProps={{ min: 5, max: 600, step: 5 }}
              value={editing?.est_minutes ?? ""}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, est_minutes: e.target.value } : s))
              }
            />
            <TextField
              label="Sort order"
              variant="standard"
              fullWidth
              type="number"
              inputProps={{ min: 0, max: 1000 }}
              value={editing?.sort_order ?? "0"}
              onChange={(e) =>
                setEditing((s) => (s ? { ...s, sort_order: e.target.value } : s))
              }
            />
            {!isCreating && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={editing?.is_active ?? true}
                  onChange={(e) =>
                    setEditing((s) => (s ? { ...s, is_active: e.target.checked } : s))
                  }
                />
                <Box sx={{ fontSize: 14 }}>{editing?.is_active ? "Active" : "Inactive"}</Box>
              </Stack>
            )}
            {formError && (
              <Box sx={{ color: "error.main", fontSize: 13 }}>{formError}</Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={{ ...greyBtn, px: 3 }}
            onClick={() => {
              setEditing(null);
              setFormError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !editing?.code ||
              !editing?.label ||
              !editing?.base_price_cents ||
              !editing?.est_minutes ||
              save.isPending
            }
            sx={primaryBtnDialog}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        open={deleteOpen}
        title="Disable price tiers"
        body={`About to disable ${selected.length} price tier${selected.length === 1 ? "" : "s"}. Tiers still assigned to an active job category will refuse the disable; reassign those categories first via Config → Job Categories. Historical jobs keep their FK and remain priced as-is.`}
        confirmLabel="Disable"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(selected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
